/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /**
   * @static @constant {number} The maximum number of products to store.
   *
   * Eight rather than Horizon's four: the Product selection widget's
   * recently-viewed tab shows the history as a carousel four cards wide, so a
   * longer history is the difference between a strip that scrolls and one that
   * exactly fills its row. Readers with less room take the slice they have room
   * for rather than the whole history — see `PredictiveSearch`, which asks the
   * search API for the four its panel draws.
   */
  static #MAX_PRODUCTS = 8;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Storage can refuse both reads and writes, and this one runs inline on
      // every product page: an uncaught throw here would take the rest of that
      // module's work with it. A history that cannot be kept is not worth a
      // broken page.
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // See `addProduct`.
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   *
   * Never throws, because every caller treats the history as ambient
   * information and none of them can do anything useful with a failure. Two
   * ways it would otherwise: storage access is denied outright — Safari with
   * cookies blocked, in-app webviews, privacy extensions, all of which raise on
   * `getItem` rather than returning null — and the stored value is the
   * shopper's own to edit, so it can be any string at all. No history is the
   * honest answer to both, and it is a state every caller already handles.
   *
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');

      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  }
}
